import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FaSun, FaMoon, FaBars, FaTimes } from 'react-icons/fa';
import { useTheme } from '../context/ThemeContext';

const Header = styled.header`
  position: sticky;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  background-color: var(--nav-bg);
  border-bottom: 1px solid var(--border-subtle);
  transition: all 0.3s ease;
`;

const NavContainer = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem 1.5rem;
`;

const Brand = styled(Link)`
  color: var(--text-primary);
  text-decoration: none;
  font-weight: 700;
  font-size: 1.35rem;
  letter-spacing: -0.5px;
  display: flex;
  align-items: center;
  gap: 0.2rem;
  font-family: var(--font-sans);

  span {
    background: var(--gradient-accent);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    font-weight: 800;
  }
`;

const NavItemsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;

  @media (max-width: 868px) {
    display: none;
  }
`;

const NavLinksList = styled.ul`
  display: flex;
  list-style: none;
  gap: 0.5rem;
  align-items: center;
  margin: 0;
  padding: 0;
`;

const NavAnchor = styled.a`
  color: ${props => (props.$active ? 'var(--text-primary)' : 'var(--text-muted)')};
  text-decoration: none;
  padding: 0.5rem 0.85rem;
  border-radius: 8px;
  font-size: 0.925rem;
  font-weight: ${props => (props.$active ? '600' : '500')};
  transition: all 0.2s ease;
  position: relative;
  cursor: pointer;

  &:hover {
    color: var(--text-primary);
    background: var(--border-subtle);
  }

  ${props =>
    props.$active &&
    `
    background: var(--border-subtle);
    color: var(--accent-cyan);
    &:after {
      content: '';
      position: absolute;
      bottom: -4px;
      left: 20%;
      right: 20%;
      height: 2px;
      background: var(--gradient-accent);
      border-radius: 2px;
    }
  `}
`;

const ThemeToggleButton = styled.button`
  background: var(--border-subtle);
  border: 1px solid var(--border-subtle);
  color: var(--text-primary);
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 1rem;

  &:hover {
    background: var(--bg-tertiary);
    transform: translateY(-2px);
    box-shadow: var(--shadow-sm);
  }

  &:focus-visible {
    outline: 2px solid var(--accent-cyan);
    outline-offset: 2px;
  }
`;

const MobileControls = styled.div`
  display: none;
  align-items: center;
  gap: 0.75rem;

  @media (max-width: 868px) {
    display: flex;
  }
`;

const MobileMenuButton = styled.button`
  background: transparent;
  border: none;
  color: var(--text-primary);
  font-size: 1.35rem;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MobileNavOverlay = styled.div`
  display: none;

  @media (max-width: 868px) {
    display: ${props => (props.isOpen ? 'flex' : 'none')};
    flex-direction: column;
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    background-color: var(--bg-secondary);
    border-bottom: 1px solid var(--border-subtle);
    padding: 1.5rem;
    box-shadow: var(--shadow-lg);
    gap: 1rem;
    animation: slideDown 0.25s ease-out;
  }

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const MobileNavAnchor = styled.a`
  color: ${props => (props.$active ? 'var(--accent-cyan)' : 'var(--text-primary)')};
  text-decoration: none;
  font-size: 1.05rem;
  font-weight: 500;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  background: ${props => (props.$active ? 'var(--border-subtle)' : 'transparent')};
  transition: background 0.2s;

  &:hover {
    background: var(--border-subtle);
  }
`;

const NAV_LINKS = [
  { label: 'Home', href: '#home', id: 'home' },
  { label: 'About', href: '#about', id: 'about' },
  { label: 'Skills', href: '#skills', id: 'skills' },
  { label: 'Projects', href: '#projects', id: 'projects' },
  { label: 'Research', href: '#research', id: 'research' },
  { label: 'Contact', href: '#contact', id: 'contact' },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const { theme, toggleTheme } = useTheme();
  const navRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Scroll to hash target when landing on home with a hash (e.g. from another page)
  useEffect(() => {
    if (location.pathname === '/' && location.hash) {
      const targetId = location.hash.replace('#', '');
      const timeout = setTimeout(() => {
        const element = document.getElementById(targetId);
        if (element) {
          const navOffset = 80;
          const offsetPosition = element.getBoundingClientRect().top + window.pageYOffset - navOffset;
          window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
          setActiveSection(targetId);
        }
      }, 150);
      return () => clearTimeout(timeout);
    }
  }, [location.pathname, location.hash]);

  // Active section spy on scroll
  useEffect(() => {
    if (location.pathname !== '/') return;

    const handleScroll = () => {
      const scrollPos = window.scrollY + 140;
      for (let i = NAV_LINKS.length - 1; i >= 0; i--) {
        const item = NAV_LINKS[i];
        const elem = document.getElementById(item.id);
        if (elem && elem.offsetTop <= scrollPos) {
          setActiveSection(item.id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  // Click outside to close mobile menu
  useEffect(() => {
    function handleClickOutside(e) {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNavClick = (e, href) => {
    setIsOpen(false);
    e.preventDefault();
    const targetId = href.replace('#', '');
    if (location.pathname !== '/') {
      // Navigate to home first, the hash-scroll effect will handle scrolling
      navigate(`/${href}`);
      return;
    }
    const element = document.getElementById(targetId);
    if (element) {
      const navOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - navOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      window.history.pushState(null, '', href);
      setActiveSection(targetId);
    }
  };

  return (
    <Header ref={navRef}>
      <NavContainer aria-label="Main Navigation">
        <Brand to="/#home" onClick={(e) => handleNavClick(e, '#home')}>
          Nimish<span>.dev</span>
        </Brand>

        <NavItemsWrapper>
          <NavLinksList>
            {NAV_LINKS.map((link) => (
              <li key={link.id}>
                <NavAnchor
                  href={link.href}
                  $active={activeSection === link.id && location.pathname === '/'}
                  onClick={(e) => handleNavClick(e, link.href)}
                >
                  {link.label}
                </NavAnchor>
              </li>
            ))}
          </NavLinksList>

          <ThemeToggleButton
            onClick={toggleTheme}
            aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
            title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
          >
            {theme === 'dark' ? <FaSun color="#f59e0b" /> : <FaMoon color="#6366f1" />}
          </ThemeToggleButton>
        </NavItemsWrapper>

        <MobileControls>
          <ThemeToggleButton
            onClick={toggleTheme}
            aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
          >
            {theme === 'dark' ? <FaSun color="#f59e0b" /> : <FaMoon color="#6366f1" />}
          </ThemeToggleButton>

          <MobileMenuButton
            onClick={() => setIsOpen(!isOpen)}
            aria-expanded={isOpen}
            aria-label="Toggle navigation menu"
          >
            {isOpen ? <FaTimes /> : <FaBars />}
          </MobileMenuButton>
        </MobileControls>

        <MobileNavOverlay isOpen={isOpen}>
          {NAV_LINKS.map((link) => (
            <MobileNavAnchor
              key={link.id}
              href={link.href}
              $active={activeSection === link.id && location.pathname === '/'}
              onClick={(e) => handleNavClick(e, link.href)}
            >
              {link.label}
            </MobileNavAnchor>
          ))}
        </MobileNavOverlay>
      </NavContainer>
    </Header>
  );
}
